import {
  Box,
  Image,
  Text,
  Flex,
  useBoolean,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useMediaQuery,
  Tooltip,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import {
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { AddIcon } from '@chakra-ui/icons';

import infoIcon from 'assets/info_icon.svg';
import noAction from 'assets/no_action_icon.png';
import {
  clearStepperFormData,
  deleteActionData,
  duplicateActionFile,
  editActionDetails,
  getActionData,
  getActionList,
  getOneActionData,
  getSelectedAction,
  getSelectedDataFileDetails,
  setActionCreateForm,
  setActionDetails,
  setActionFormEdit,
  setClearSelectedAreasTable,
  versionData,
} from 'redux/DataImportSlice';
import DataSet from 'components/Icons/DataSet';
import Update from 'components/Icons/Update';
import CustomInputField from 'components/CustomInput';
import DataImportIcon from 'components/Icons/DataImportIcon';
import DeleteModal from 'components/Modals/DeleteModal';

const LazyMappingDimensionModal = lazy(() => import('./MappingDimensionModal'));
const LazyMappingDataModal = lazy(() => import('./MappingDataModel'));

/** Chakra MenuList exit animation is ~100ms; keep the row elevated until it finishes to avoid sibling “jump”. */
const ACTION_MENU_Z_INDEX_CLEAR_MS = 130;

/** Fixed box for action-type icons — shared so rows align; glyphs are centred inside. */
const ACTION_ROW_ICON_BOX_PX = '29px';
/**
 * `DataImportIcon` artwork fills the viewBox more than `DataSet`; use a slightly smaller
 * render size so actuals/forecast rows match dimension icons optically (see screenshots).
 */
const ACTION_ROW_DATA_IMPORT_ICON_SIZE_PX = 20;
/** Dimension / grid icon — tuned to pair with the data-import size above inside the same box. */
const ACTION_ROW_DIMENSION_ICON_SIZE_PX = 24;

/**
 * Title row `padding-right`: only the [...] overlay when idle so names use nearly full width;
 * extra space when hovered (Run + play icon + menu) so text ellipsizes before the controls.
 */
const ACTION_TITLE_PADDING_RIGHT_IDLE = '2.75rem';
const ACTION_TITLE_PADDING_RIGHT_HOVER = '6rem';

const commonStyles = {
  display: 'flex',
  alignItems: 'center',
};

const ActionData = ({ selectedFileDetails, setactionCreate }: any) => {
  /** Only drives the [...] menu item font sizes; action row labels stay `xs` at all widths. */
  const [isSmallDevice] = useMediaQuery('(max-width: 900px)');
  const dispatch: any = useDispatch();
  const selectedSourceDetails = useSelector(getSelectedDataFileDetails);
  const actionData = useSelector(getActionList);
  const [renameItemId, setRenameItemId] = useState<number | null>(null);
  const [renameItemValue, setRenameItemValue] = useState<string | null>(null);
  const [dimensionMappingModal, setDimensionMappingModal] = useBoolean();
  const [dataMappingModal, setDataMappingModal] = useBoolean();
  const [selectedActionId, setSelectedActionId] = useState<number | null>(null);
  const [selectedMapType, setSelectedMapType] = useState('');
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteIdModal, setDeleteIdModal]: any = useState(null);
  const [DeleteNameModal, setDeleteNameModal] = useState('');
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);
  /** Row whose [...] menu is open — lifts that card above siblings so the list is not covered. */
  const [actionMenuOpenId, setActionMenuOpenId] = useState<number | null>(null);
  const actionMenuZIndexClearTimeoutReference = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataSourceId = selectedSourceDetails?.id;
  const selectedAction = useSelector(getSelectedAction);
  useEffect(() => {
    if (dataSourceId) {
      dispatch(versionData(dataSourceId));
      dispatch(getActionData(Number(dataSourceId)));
    }
  }, [dataSourceId]);
  useEffect(() => {
    return () => {
      if (actionMenuZIndexClearTimeoutReference.current) {
        clearTimeout(actionMenuZIndexClearTimeoutReference.current);
      }
    };
  }, []);

  const renameItem = (event: any, id: number) => {
    event.stopPropagation();
    if (renameItemId === id) {
      setRenameItemId(0);
    }
    setRenameItemId(id);
  };

  const duplicateItem = async (event: any, id: number) => {
    event.stopPropagation();
    await dispatch(duplicateActionFile({ dataSourceId, actionId: id }));
    await dispatch(getActionData(dataSourceId));
  };
  const UpdateItemApi = async (item_id: Number) => {
    const requestBody = {
      name: renameItemValue,
    };
    await dispatch(
      editActionDetails({
        dataSourceId,
        actionId: item_id,
        request_body: requestBody,
        toaster: {
          successMessage: 'Successfully updated',
          errorMessage: 'An Source with this name already exists',
        },
      }),
    );
    await setRenameItemId(0);
    await dispatch(getActionData(dataSourceId));
  };

  const onKeyPressAction = async (event: any, item_id: number) => {
    event.stopPropagation();
    if (event.keyCode === 13) {
      await UpdateItemApi(item_id);
      setRenameItemValue(null);
    }
    if (event.key === 'Escape') setRenameItemValue(null);
  };
  const deleteScenarioModal = async (event: any, id: number, name: any) => {
    event.stopPropagation();
    setIsDeleteModal(true);
    setDeleteIdModal(id);
    setDeleteNameModal(name);
  };
  const onDeleteModel = async () => {
    await dispatch(deleteActionData({ dataSourceId, actionId: deleteIdModal }));
    await dispatch(getActionData(dataSourceId));
    await setIsDeleteModal(false);
    await setDeleteIdModal(null);
    await setDeleteNameModal('');
  };
  const run = (item: any) => {
    if (item.action_type === 'dimension') {
      setDimensionMappingModal.on();
    } else {
      setDataMappingModal.on();
    }

    setSelectedActionId(item?.id);
    setSelectedMapType(item?.action_type);
    setScenarioId(item?.scenario_id);
  };

  return (
    <>
      <Flex
        flexDirection={'column'}
        maxH={`70vh`}
        overflowX={'hidden'}
        overflowY={'auto'}
        className='scroll'
        mt={'1rem'}
      >
        <Flex flexDirection={'column'} maxH={'full'} pe={2}>
          {actionData && actionData?.length > 0 ? (
            actionData.map((item: any, index: number) => (
              <Box
                w={'100%'}
                minH='45px'
                position='relative'
                {...commonStyles}
                px={3}
                gap={3}
                borderRadius={'9px'}
                width='full'
                cursor={'pointer'}
                mb={'11.50px'}
                key={item?.id ?? index}
                zIndex={actionMenuOpenId === item?.id ? 20 : undefined}
                bg={selectedAction?.actionId === item?.id ? '#4BB6B9' : '#7163D0'}
                boxShadow='2px 3px 5px rgba(0, 0, 0, 0.25)'
                onMouseEnter={() => setHoveredItemId(item?.id)}
                onMouseLeave={() => setHoveredItemId(null)}
              >
                {/*
                  Title row uses full width; Run + menu sit in an absolute strip on the right
                  so hover never changes flex widths (no side column / preview jumping).
                */}
                <Flex
                  w='100%'
                  minW={0}
                  gap={3}
                  align={'center'}
                  minH='45px'
                  pr={
                    hoveredItemId === item?.id && renameItemId !== item?.id
                      ? ACTION_TITLE_PADDING_RIGHT_HOVER
                      : ACTION_TITLE_PADDING_RIGHT_IDLE
                  }
                  transition='padding-right 0.15s ease-out'
                  onClick={() => {
                    dispatch(setActionDetails({ actionId: item.id }));
                    dispatch(getOneActionData({ dataSourceId, actionId: item.id }));
                  }}
                >
                  <Flex
                    boxSize={ACTION_ROW_ICON_BOX_PX}
                    flexShrink={0}
                    align='center'
                    justify='center'
                  >
                    {item.action_type === 'dimension' ? (
                      <DataSet
                        color={
                          selectedAction?.actionId === item?.id
                            ? 'rgba(101, 98, 207, 1)'
                            : '#4BB6B9'
                        }
                        size={ACTION_ROW_DIMENSION_ICON_SIZE_PX}
                      />
                    ) : (
                      <DataImportIcon
                        color={
                          selectedAction?.actionId === item?.id
                            ? 'rgba(101, 98, 207, 1)'
                            : '#4BB6B9'
                        }
                        size={ACTION_ROW_DATA_IMPORT_ICON_SIZE_PX}
                      />
                    )}
                  </Flex>
                  <Box flex={1} minW={0} overflow='hidden'>
                    {renameItemId === item?.id ? (
                      <CustomInputField
                        alignContent='center'
                        fontSize='xs'
                        whiteSpace='nowrap'
                        overflow='hidden'
                        width={'100%'}
                        outline={'none !important'}
                        boxShadow={'none !important'}
                        border={'1px #fff solid !important'}
                        padding={'13px 5px 13px 10px'}
                        defaultValue={item?.name}
                        height='0.9rem'
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          setRenameItemValue(event.target.value);
                        }}
                        onKeyDown={(event: React.ChangeEvent<HTMLInputElement>) => {
                          if (event.target.value.trim().length > 0)
                            onKeyPressAction(event, item?.id);
                        }}
                        _placeholder={{ color: 'white' }}
                        autoFocus
                        color='white'
                        onBlur={() => {
                          setRenameItemId(0);
                        }}
                        required
                      />
                    ) : (
                      <Tooltip label={item?.name} openDelay={400} isDisabled={!item?.name}>
                        <Text
                          as='span'
                          display='block'
                          fontSize='xs'
                          lineHeight='short'
                          whiteSpace='nowrap'
                          overflow='hidden'
                          textOverflow='ellipsis'
                        >
                          {item?.name}
                        </Text>
                      </Tooltip>
                    )}
                  </Box>
                </Flex>
                {/*
                  zIndex: strip stays above the title text but below portaled menus (MenuList uses 2000).
                  Without a high MenuList z-index, the next action card can paint over the dropdown.
                */}
                <Flex
                  position='absolute'
                  top={0}
                  right={0}
                  bottom={0}
                  align='center'
                  justify='flex-end'
                  gap={2}
                  pl={3}
                  pr={3}
                  zIndex={2}
                  borderTopRightRadius='9px'
                  borderBottomRightRadius='9px'
                  bg={selectedAction?.actionId === item?.id ? '#4BB6B9' : '#7163D0'}
                  pointerEvents='auto'
                >
                  {hoveredItemId === item?.id && renameItemId !== item?.id && (
                    <Flex
                      role='button'
                      tabIndex={0}
                      align='center'
                      gap={1}
                      onClick={(mouseEvent: MouseEvent<HTMLDivElement>) => {
                        mouseEvent.stopPropagation();
                        run(item);
                      }}
                      onKeyDown={(keyEvent: KeyboardEvent) => {
                        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                          keyEvent.preventDefault();
                          keyEvent.stopPropagation();
                          run(item);
                        }
                      }}
                      cursor='pointer'
                      aria-label='Run import'
                    >
                      <Text fontSize='xs' userSelect='none'>
                        Run
                      </Text>
                      <Update color={selectedAction?.actionId === item?.id ? 'white' : '#4BB6B9'} />
                    </Flex>
                  )}
                  <Menu
                    closeOnSelect={true}
                    variant='primary'
                    placement='bottom-end'
                    strategy='fixed'
                    gutter={4}
                    onOpen={() => {
                      if (actionMenuZIndexClearTimeoutReference.current) {
                        clearTimeout(actionMenuZIndexClearTimeoutReference.current);
                        actionMenuZIndexClearTimeoutReference.current = null;
                      }
                      setActionMenuOpenId(item.id);
                    }}
                    onClose={() => {
                      if (actionMenuZIndexClearTimeoutReference.current) {
                        clearTimeout(actionMenuZIndexClearTimeoutReference.current);
                      }
                      const closedActionId = item.id;
                      actionMenuZIndexClearTimeoutReference.current = setTimeout(() => {
                        actionMenuZIndexClearTimeoutReference.current = null;
                        setActionMenuOpenId((openId) =>
                          openId === closedActionId ? null : openId,
                        );
                      }, ACTION_MENU_Z_INDEX_CLEAR_MS);
                    }}
                  >
                    <MenuButton
                      as={Button}
                      border={'none'}
                      width={'fit-content'}
                      padding={0}
                      minW={'fit-content'}
                      onClick={(mouseEvent: MouseEvent<HTMLButtonElement>) =>
                        mouseEvent.stopPropagation()
                      }
                    >
                      <Image
                        src={infoIcon}
                        w='18px'
                        h='18px'
                        className='infoButton'
                        cursor={'pointer'}
                      />
                    </MenuButton>
                    <MenuList zIndex={2000}>
                      <MenuItem
                        onClick={(event) => renameItem(event, item.id)}
                        fontSize={isSmallDevice ? '11px' : '14px'}
                        fontFamily='regular_roboto'
                      >
                        Rename
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          dispatch(getOneActionData({ dataSourceId, actionId: item.id }));
                          setactionCreate(true);
                          dispatch(setActionCreateForm(true));
                          dispatch(
                            setActionFormEdit({
                              isEditForm: true,
                              actionId: item?.id,
                              actionType: item?.action_type,
                              scenarioId: item?.scenario_id,
                            }),
                          );
                        }}
                        fontSize={isSmallDevice ? '11px' : '14px'}
                        fontFamily='regular_roboto'
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={(event) => {
                          deleteScenarioModal(event, item?.id, item?.name);
                        }}
                        fontSize={isSmallDevice ? '11px' : '14px'}
                        fontFamily='regular_roboto'
                      >
                        Delete
                      </MenuItem>
                      <MenuItem
                        onClick={(event) => duplicateItem(event, item.id)}
                        fontSize={isSmallDevice ? '11px' : '14px'}
                        fontFamily='regular_roboto'
                      >
                        Duplicate
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
              </Box>
            ))
          ) : (
            <Flex flexDirection={'column'} mt={1}>
              <Box
                minH='40px'
                {...commonStyles}
                px={3}
                gap={3}
                borderRadius={'9px'}
                width='full'
                border={'1px dashed rgba(255, 255, 255, 0.40)'}
                cursor={'pointer'}
                onClick={() => {
                  setactionCreate(true);
                  dispatch(setActionCreateForm(true));
                }}
                boxShadow='2px 3px 5px rgba(0, 0, 0, 0.25)'
              >
                + Add action
              </Box>
              <Box
                width='full'
                display={'flex'}
                justifyContent={'end'}
                alignItems={'center'}
                py={5}
              >
                <Box width={'50%'}>
                  <Text textAlign={'start'}>
                    Now add an action to your source to import data from your file
                  </Text>
                </Box>
                <Box width={'40%'}>
                  <Image h={30} w={30} src={noAction} />
                </Box>
              </Box>
            </Flex>
          )}
        </Flex>
      </Flex>
      {actionData && selectedFileDetails?.deatsilsPage === true && actionData?.length > 0 && (
        <Flex
          ps={'10px'}
          pt={'1.5rem'}
          cursor={'pointer'}
          onClick={async () => {
            await dispatch(clearStepperFormData());
            await dispatch(setClearSelectedAreasTable());
            await setactionCreate(true);
            await dispatch(setActionCreateForm(true));
          }}
        >
          <AddIcon />
        </Flex>
      )}
      <DeleteModal
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        itemName={DeleteNameModal}
        onDelete={onDeleteModel}
      />
      <Suspense fallback={<div>Loading...</div>}>
        {dimensionMappingModal && (
          <LazyMappingDimensionModal
            mappingModal={dimensionMappingModal}
            setMappingModal={setDimensionMappingModal}
            selectedActionId={selectedActionId}
            dataSourceId={dataSourceId}
            selectedMapType={selectedMapType}
            scenarioId={scenarioId}
          />
        )}
      </Suspense>
      <Suspense fallback={<div>Loading...</div>}>
        {dataMappingModal && (
          <LazyMappingDataModal
            mappingModal={dataMappingModal}
            setMappingModal={setDataMappingModal}
            selectedActionId={selectedActionId}
            dataSourceId={dataSourceId}
            selectedMapType={selectedMapType}
            scenarioId={scenarioId}
          />
        )}
      </Suspense>
    </>
  );
};

export default ActionData;
